import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RESPONSE_CODE } from "../../../api/network/constants";
import {
  useGetReportSpecificFieldsQuery,
  useRunCustomReportQuery,
} from "../../../api/network/reportApiServices";
import { TModalsState } from "../../../api/store/commonSlice";
import { CustomWidget } from "../../../api/types/Overview";
import { TReportField } from "../../../api/types/Report";
import svgIcons from "../../../assets/svg/svgIcons";
import {
  APP_CONFIG,
  REPORT_FILTER_BY_OPTION_VALUES,
  REPORT_TYPES,
} from "../../../constants/constants";
import useConversion from "../../../hooks/conversion";
import { jsTimeToDBTime } from "../../../utils/dateTime";
import { REPORT_FIELD_OUTPUT_TYPES } from "../../reports/constants";
import ChartWrapper from "../components/chartWrapper";
import ModalSwitchChartType from "../components/modalSwitchChartType";
import { IWidgetProps } from "../components/overviewDragDropColumnItem";
import WidgetChartAreaGenerator from "../components/widgetChartAreaGenerator";
import WidgetFilterSelector from "../components/widgetFilterSelector";
import { useOverviewContext } from "../context/overview.context";
import { useChartUtils } from "../hooks/chartUtils";
import { EWidgetChartTypes } from "../types/overview.types";
import { getChartTypeNameById, verticalLabelFormatter } from "../utils/chart";

const tableMinHeightClass = "min-h-[100px]";
const tableMaxHeightClass = "max-h-[224px]";

const debugComponent = false;

interface IWidgetCustom extends IWidgetProps {
  widgetDetails: CustomWidget;
  showDetails: boolean;
  setShowDetails: (value: boolean) => void;
}

const WidgetCustom: FC<IWidgetCustom> = ({
  widgetDetails,
  widgetType,
  showDetails,
  setShowDetails,
}) => {
  const { t: tOverview } = useTranslation("translation", {
    keyPrefix: "overviewScreen",
  });
  const { t } = useTranslation("translation", {
    keyPrefix: `overviewScreen.widgets.enum_${widgetType}`,
  });

  const {
    allTimeFilters,
    timeFilter,
    refetchDashboardList,
    isLoadingUpdateDashboard,
    isFetchingCustomList,
    refetchCustomWidgetList,
    customList,
    selectedDataType,
    setSelectedDataType,
    allAssetsSelected,
    selectedAssets,
    setAllAssetsSelected,
    setSelectedAssets,
  } = useOverviewContext();

  // replica of getTimeRange in `useGroupsAndAssetsUtils` under `overviewScreenUtils.ts`
  const getTimeRange = (timeFilter: any) => {
    const filter = allTimeFilters.find((filter) => filter.slug === timeFilter);
    if (!filter)
      return {
        start_time: undefined,
        end_time: undefined,
        ds_time_range: undefined,
      };

    const [start, end] = filter.timeRange || [];
    return {
      start_time: start ? jsTimeToDBTime(start) : undefined,
      end_time: end ? jsTimeToDBTime(end) : undefined,
      ds_time_range: filter.value,
    };
  };

  const widget = customList?.find(
    (item) => item.id === widgetDetails?.custom_widget
  );

  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const [selectedChartType, setSelectedChartType] = useState<EWidgetChartTypes>(
    // widget?.graph_type
    !!widget?.graph_type
      ? getChartTypeNameById(Number(widget?.graph_type)) ||
          EWidgetChartTypes.VERTICAL_BAR_GRAPH
      : EWidgetChartTypes.VERTICAL_BAR_GRAPH
  );

  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const reportId = widget?.report;

  const graphXAxisDataKey = widget?.x_axis ?? "0"; // 'Asset ID';
  const graphYAxisDataKey = widget?.y_axis ?? "0"; // 'Miles left to Service';

  const {
    data: dataSpecificReport,
    isFetching: isFetchingSpecificReport,
    isError: isSpecificReportError,
    error: specificReportError,
    isUninitialized: isUninitializedSpecificReport,
    refetch: refetchDataSpecificReport,
  } = useGetReportSpecificFieldsQuery(
    {
      type: REPORT_TYPES.CUSTOM,
      report_id: reportId ?? 0,
    },
    {
      skip: !reportId,
    }
  );

  const graphYAxisDataKeyId = useMemo(() => {
    return (
      dataSpecificReport?.report_fields?.find(
        (field) => field.identifier === graphYAxisDataKey
      )?.field.id || 0
    );
  }, [dataSpecificReport?.report_fields, graphYAxisDataKey]);

  const {
    data: dataReport,
    isFetching,
    isError,
    error: reportError,
  } = useRunCustomReportQuery(
    {
      id: reportId?.toString() ?? "",
      fields: dataSpecificReport?.report_fields
        ?.map((field) => {
          return field.id;
        })
        .join(","),
      page: 1,
      page_size: 10,
      report_fields: dataSpecificReport?.report_fields?.map(
        (field: TReportField) => {
          const groupByIsActive = field.identifier === widget?.x_axis;
          return {
            field: parseInt(field.field.all_field_id ?? "0"),
            // aggregation param
            ...(!!field.aggregate_function
              ? {
                  aggregate_function: field.aggregate_function,
                  column_type: field.column_type,
                  value: field.value,
                  operator: field.operator,
                }
              : {}),
            // group by param
            ...(groupByIsActive
              ? {
                  is_group_by: field.identifier === widget.x_axis,
                  group_order: field.group_order,
                }
              : {}),
          };
        }
      ),
      // device groups param
      ...(selectedDataType === REPORT_FILTER_BY_OPTION_VALUES.GROUP && {
        device_groups: Array.isArray(selectedAssets)
          ? selectedAssets.join(",")
          : "",
      }),
      // assets param
      ...(selectedDataType === REPORT_FILTER_BY_OPTION_VALUES.ASSET && {
        device_id: Array.isArray(selectedAssets)
          ? selectedAssets.join(",")
          : "",
      }),
      // time range param
      ...getTimeRange(timeFilter),
      // order by param
      ordering:
        sortOrder === "asc"
          ? `${graphYAxisDataKeyId}`
          : `-${graphYAxisDataKeyId}`,
      // for showing average line
      ...(widget?.show_average && !!widget?.y_axis
        ? {
            custom_widget_id: widget?.id,
            calculate_average_using: widget?.y_axis,
          }
        : {}),
    },
    {
      skip:
        isUninitializedSpecificReport ||
        isFetchingSpecificReport ||
        graphYAxisDataKeyId === 0 ||
        !reportId?.toString() ||
        reportId?.toString() === "",
      refetchOnMountOrArgChange: true,
    }
  );

  // if 404 error, refetch dashboard list
  useEffect(() => {
    if (
      isError &&
      (reportError as FetchBaseQueryError)?.status === RESPONSE_CODE.NOT_FOUND
    ) {
      refetchDashboardList();
    }
  }, [isError, reportError, refetchDashboardList]);

  const [computedChartData, setComputedChartData] = useState<any[]>([]);
  const [chartGroupingLabels, setChartGroupingLabels] = useState<string[]>([]);
  const { checkAndFetchDrillDownData } = useChartUtils();
  const { convertToUnit } = useConversion();
  useEffect(() => {
    if (!!dataReport && !isFetching) {
      // disabled this part, computed chart data is set by checkAndFetchDrillDownData
      // setComputedChartData(
      //   [...(dataReport?.results || [])]?.sort(
      //     (a, b) => a[graphYAxisDataKey] - b[graphYAxisDataKey]
      //   )
      // );
      const initialChartData = [
        ...(dataReport?.results.map((resultItem) => {
          const fieldData = dataSpecificReport?.report_fields?.find(
            (field) => field.identifier === graphYAxisDataKey
          );
          return {
            [graphXAxisDataKey]: resultItem[graphXAxisDataKey],
            // [graphYAxisDataKey]: resultItem[graphYAxisDataKey],
            [graphYAxisDataKey]: parseFloat(
              convertToUnit(
                fieldData?.output_type || REPORT_FIELD_OUTPUT_TYPES.TEXT,
                resultItem[graphYAxisDataKey],
                fieldData?.convert_to_whole_number
              )?.finalData || "0"
            ),
          };
        }) || []),
      ];
      // ?.sort(
      //   (a, b) => a[graphYAxisDataKey] - b[graphYAxisDataKey]
      // );

      if (!!widget?.group_by) {
        setFetchingWidgetData(true);
        // this call is important to set the data and fetchingWidgetData flag
        checkAndFetchDrillDownData({
          retrievedComputedChartData: initialChartData?.map((item) => {
            // removing the initial y-axis data value corrects the y-axis scale based on display values when sub-grouping is enabled
            return {
              ...item,
              // however the initial y-axis data value is needed for correct y-axis scale in stacked bar graph
              // (thus not removing it in case of stacked bar graph)
              ...(selectedChartType !== EWidgetChartTypes.STACKED_BAR_GRAPH
                ? { [graphYAxisDataKey]: undefined } // Note: this could be unnecessary but has shown correct results with testing
                : {}),
            };
          }),
          dataSpecificReport,
          dataReport,
          isFetching,
          setChartGroupingLabels,
          setComputedChartData,
          setFetchingWidgetData,
          reportId,
          widget,
          graphXAxisDataKey,
          graphYAxisDataKey,
        });
      } else {
        setComputedChartData(initialChartData);
        setFetchingWidgetData(false);
      }
    }
  }, [dataReport, isFetching, dataSpecificReport]);
  // console.log("computedChartData", computedChartData);
  // console.log("chartGroupingLabels", chartGroupingLabels);
  // console.log("graphXAxisDataKey", graphXAxisDataKey);
  // console.log("graphYAxisDataKey", graphYAxisDataKey);

  // used as y-axis label in the chart when sub-grouping is enabled
  const [groupByLabelWithMaxValue, setGroupByLabelWithMaxValue] =
    useState(graphYAxisDataKey);
  useEffect(() => {
    if (computedChartData.length > 0 && !!widget?.group_by) {
      let maxValue = 0;
      let maxValueLabel = "";
      chartGroupingLabels.forEach((label) => {
        const max = Math.max(
          ...computedChartData
            .filter((item) => !!item[label])
            .map((item) => item[label])
        );
        if (max > maxValue) {
          maxValue = max;
          maxValueLabel = label;
        }
      });
      setGroupByLabelWithMaxValue(maxValueLabel);
    }
  }, [computedChartData, chartGroupingLabels, widget?.group_by]);

  const [fetchingWidgetData, setFetchingWidgetData] = useState(false);
  useEffect(() => {
    if (reportId && !dataReport && !isFetching) {
      setFetchingWidgetData(true);
    }
  }, [reportId, dataReport, isFetching]);
  // when data is fetched, check report definition:
  // if sub-grouping is enabled, further call the sub-grouping APIs for each row
  // if sub-grouping is disabled, display the data as is
  // `checkAndFetchDrillDownData` is moved to chartUtils hook

  const eligibleChartVariables: TReportField[] =
    dataSpecificReport?.report_fields?.filter((field) => field.is_group_by) ??
    [];

  const selectedGroup = useMemo(() => {
    return eligibleChartVariables.find(
      (group) => group.identifier === widget?.x_axis
    );
  }, [widget, eligibleChartVariables]);

  const MemoizedWidgetFilterSelector = useMemo(
    () => {
      const limitWidth = true;
      const filterOptions = [
        {
          label: `Highest ${
            dataSpecificReport?.report_fields?.find(
              (field) => field.identifier === graphYAxisDataKey
            )?.name || "-"
          }`,
          value: "desc",
        },
        {
          label: `Lowest ${
            dataSpecificReport?.report_fields?.find(
              (field) => field.identifier === graphYAxisDataKey
            )?.name || "-"
          }`,
          value: "asc",
        },
      ];
      return (
        <div className="mb-4 flex justify-between">
          <WidgetFilterSelector
            limitWidth={limitWidth}
            selectedFilterLabel={
              filterOptions.find((option) => option.value === sortOrder)?.label
            }
            filters={filterOptions}
            setFilter={(newValue) => setSortOrder(newValue as "asc" | "desc")}
          />
        </div>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sortOrder, dataSpecificReport?.report_fields, graphYAxisDataKey]
  );

  // check the current status of custom report to ensure that group by, sub group by and aggregation are set
  const customReportGroupByIdentifier =
    dataSpecificReport?.report_fields?.find(
      (field) => !!field.is_group_by && field.group_order === 1
    )?.identifier || "";
  const customReportSubGroupByIdentifier =
    dataSpecificReport?.report_fields?.find(
      (field) => !!field.is_group_by && field.group_order === 2
    )?.identifier || "";
  const customReportAggregateFunctionIdentifiers =
    dataSpecificReport?.report_fields
      ?.filter((field) => !!field.aggregate_function)
      ?.map((field) => field.identifier) || [];
  const customReportSupportsWidgets =
    !!customReportGroupByIdentifier &&
    // !!customReportSubGroupByIdentifier && // disabled to allow display of chart even if grouping is unavailable
    customReportAggregateFunctionIdentifiers.length > 0;
  const customReportAlignsWithWidgetSettings =
    customReportGroupByIdentifier === widget?.x_axis &&
    // customReportSubGroupByIdentifier === widget?.group_by && // disabled to allow display of chart even if grouping is unavailable
    !!widget?.y_axis &&
    customReportAggregateFunctionIdentifiers.includes(widget?.y_axis);

  // details table removed as custom widget details button redirects to the custom report page
  // const DetailsTable

  return (
    <div className="mt-2">
      {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
        <pre className="absolute text-[8px] text-field-error-dark -mt-2">
          reportId: {reportId}
        </pre>
      )}
      {/* Filter */}
      {MemoizedWidgetFilterSelector}
      {(!!dataReport && dataReport?.results?.length > 0) ||
      isFetching ||
      fetchingWidgetData ? (
        <>
          {isFetching || fetchingWidgetData ? ( // isFetching
            <ChartWrapper heightClass="pt-[45%]">
              <img
                src={svgIcons.LoadingAnimation}
                alt="laoding"
                className="absolute size-8 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
              />
            </ChartWrapper>
          ) : (
            <>
              {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && debugComponent && (
                <pre className="absolute top-full text-[8px] bg-gray-200 z-50">
                  customReportGroupByIdentifier:{" "}
                  {JSON.stringify(customReportGroupByIdentifier)}
                  <br />
                  customReportSubGroupByIdentifier:{" "}
                  {JSON.stringify(customReportSubGroupByIdentifier)}
                  <br />
                  customReportAggregateFunctionIdentifier:{" "}
                  {JSON.stringify(
                    customReportAggregateFunctionIdentifiers,
                    null,
                    2
                  )}
                  <br />
                  customList:
                  <br />
                  <span className="bg-black/10 block h-24 overflow-auto">
                    {JSON.stringify(customList, null, 2)}
                  </span>
                  <br />
                  graphXAxisDataKey: {graphXAxisDataKey}
                  <br />
                  graphYAxisDataKey: {graphYAxisDataKey}
                  <br />
                  group_by: {widget?.group_by}
                  <br />
                  groupingLabels: {chartGroupingLabels.join(", ")}
                  <br />
                  computedChartData:
                  {JSON.stringify(computedChartData, null, 2)}
                  <br />
                  groupByLabelWithMaxValue: {groupByLabelWithMaxValue}
                </pre>
              )}
              {customReportSupportsWidgets &&
              customReportAlignsWithWidgetSettings ? (
                <>
                  {/* Render the chart */}
                  <WidgetChartAreaGenerator
                    barChartMargin={
                      selectedChartType ===
                      EWidgetChartTypes.HORIZONTAL_BAR_GRAPH
                        ? { top: 0, right: 10, bottom: 0, left: -20 }
                        : { top: 0, right: 10, bottom: 20, left: -30 }
                    }
                    cellItemsForSectionColor={
                      [...(dataReport?.results || [])]
                      // ?.sort((a, b) => a[graphYAxisDataKey] - b[graphYAxisDataKey])
                    }
                    chartGroupingLabels={chartGroupingLabels}
                    chartHighlightLabel={`Total ${selectedGroup?.name}`}
                    chartHighlightValue={dataReport?.count?.toString() ?? "0"}
                    chartWrapperHeightClass={
                      selectedChartType ===
                      EWidgetChartTypes.HORIZONTAL_BAR_GRAPH
                        ? "pt-[100%]"
                        : "pt-[45%]"
                    }
                    chartType={
                      selectedChartType
                      // getChartTypeNameById(selectedChartType ?? 1) ??
                      // EWidgetChartTypes.VERTICAL_BAR_GRAPH
                    }
                    computedChartData={computedChartData}
                    customWidget={widget}
                    graphXAxisDataKey={graphXAxisDataKey}
                    graphYAxisDataKey={
                      selectedChartType === EWidgetChartTypes.STACKED_BAR_GRAPH
                        ? // in case of stacked bar graph, use the initial y-axis data key
                          graphYAxisDataKey
                        : // in case of other chart types, use the groupByLabelWithMaxValue if available
                          groupByLabelWithMaxValue || graphYAxisDataKey
                    }
                    legendValueFormatter={(value: any) =>
                      (isNaN(value) ? 0 : value)?.toFixed(2)?.replace(".00", "")
                    }
                    legendHeading={selectedGroup?.name}
                    legendVerticalAlignClass="justify-start"
                    showYZeroReferenceLine={true}
                    t={(value: any) => value}
                    usePNBarColors={false}
                    widgetType={widgetType}
                    xAxisAngle={-45}
                    xAxisTickLabelWidth={40}
                    xAxisTickUsesTranslation={false}
                    yAxisTickFormatter={(value) =>
                      value === 0 ? "0" : verticalLabelFormatter(value)
                    }
                    // show average line
                    {...(!!widget?.show_average &&
                    typeof dataReport?.results?.[0]?.avg_value === "number"
                      ? {
                          averageReferenceLineValue:
                            dataReport?.results?.[0]?.avg_value,

                          showAverageReferenceLineWithLabel: tOverview(
                            "charts.average_label"
                          ),
                        }
                      : {})}
                  />
                </>
              ) : (
                <>
                  {/* Current report configuration does not align with saved/expected widget options */}
                  <ChartWrapper heightClass="pt-[45%]">
                    <p className="absolute w-10/12 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-sm text-center text-gray-400">
                      Current report configuration does not align with the
                      saved/expected widget options.{" "}
                      {customReportSupportsWidgets
                        ? "To fix this, please edit the widget."
                        : "To fix this, please edit the report."}
                    </p>
                  </ChartWrapper>
                </>
              )}
            </>
          )}

          {/* Table */}
          {isFetching || fetchingWidgetData ? (
            <div
              className={`${tableMinHeightClass} ${tableMaxHeightClass} flex justify-center items-center relative`}
            >
              <img
                src={svgIcons.LoadingAnimation}
                alt="laoding"
                className="absolute size-8 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
              />
            </div>
          ) : (
            <></>
          )}
        </>
      ) : (
        <p className="p-6 text-center text-sm text-heading-gray888">
          {tOverview("no_widget_data")}
        </p>
      )}
      {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
        <pre className="text-[8px]">
          widgetType: {widgetType?.toString()} {typeof widgetType}
          <br />
          modalsState.showSwitchChartType:{" "}
          {modalsState.showSwitchChartType?.toString()}
          <br />
          modalsState.switchChartTypeForWidget:{" "}
          {modalsState.switchChartTypeForWidget?.toString()}{" "}
          {typeof modalsState.switchChartTypeForWidget}
          <br />
          modalsState.switchChartTypeForCustomWidgetId:{" "}
          {modalsState.switchChartTypeForCustomWidgetId?.toString()}{" "}
          {typeof modalsState.switchChartTypeForCustomWidgetId}
          <br />
          selectedChartType: {selectedChartType?.toString()} <br />
          widget?.graph_type: {widget?.graph_type?.toString()}
          <br />
          computedChartData: {JSON.stringify(computedChartData, null, 2)}
          <br />
          graphXAxisDataKey: {graphXAxisDataKey}
          <br />
          graphYAxisDataKey: {graphYAxisDataKey}
          <br />
          groupByLabelWithMaxValue: {groupByLabelWithMaxValue}
        </pre>
      )}
      {modalsState.showSwitchChartType &&
        modalsState.switchChartTypeForWidget === widgetType &&
        modalsState.switchChartTypeForCustomWidgetId === widget?.id && (
          <ModalSwitchChartType
            selectedChartType={selectedChartType}
            onChartTypeChange={(newChartType) => {
              setSelectedChartType(newChartType);
            }}
          />
        )}
    </div>
  );
};
export default WidgetCustom;
